import {
  getRectangularOverhangs,
  hasDiagonalEdge,
  offsetRoofPerimeter,
  offsetRoofPerimeterOld,
} from "building-generation/elements/roofs/roofPerimeters";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type {
  RoofVolumeGenerationStageInput,
  RoofVolumeGenerationStageResult,
} from "building-types/roofVolumes/roofVolumeGenerationTypes";
import { roofPerimetersNew } from "doc/utils/roofTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import { offsetPolyLine2Variable } from "geom/offset";
import {
  polyLine2AABB2,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeTangent,
  polyLine2FromAABB2,
  polyLine2Rotate,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2AngleBetween } from "geom/vec2";

/**
 * Generates the different perimeters of a multi-gable roof.
 */
export function generateRoofVolumePerimeters({
  roofVolumeOptions,
  roofVolume,
}: RoofVolumeGenerationStageInput): RoofVolumeGenerationStageResult {
  const {
    inputPerimeters,
    roofInputSettings,
    peakDirection,
    startEdge,
    endEdge,
    wallPlanes = false,
  } = roofVolumeOptions;
  const { inputPerimeter, boundingPerimeter, exactPerimeter } = inputPerimeters;
  const { overhangs, rectangularOverhangs, exteriorWallOffsetsWithFinish, exteriorWallOffsetsFromOverhangDatum } =
    roofVolume;
  const { edgeOverhangs } = overhangs;
  const { startOverhang, endOverhang, leftOverhang, rightOverhang } = rectangularOverhangs;
  const {
    wallSettings,
    roofSettings: { overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING },
  } = roofInputSettings;

  const exteriorWallOffsetFromOverhangDatum = getExteriorWallOffset(wallSettings, overhangDatum);

  // Wall-plane exact outlines already sit on the wall core. Do not re-apply
  // finish/substrate offsets when building clip perimeters.
  const wallsPerimeter = wallPlanes
    ? polyLine2Copy(exactPerimeter)
    : offsetPolyLine2Variable(exactPerimeter, exteriorWallOffsetsWithFinish);
  const offsetWallPerimeter = polyLine2Copy(wallsPerimeter);
  const wallsPerimeterWRTOverhangDatum = offsetPolyLine2Variable(exactPerimeter, exteriorWallOffsetsFromOverhangDatum);

  const offsetExactPerimeter = offsetRoofPerimeter(exactPerimeter, edgeOverhangs);
  const [mainAxisX, mainAxisY] = polyLine2EdgeTangent(boundingPerimeter, 0);
  const angle = vec2AngleBetween(1.0, 0.0, mainAxisX, mainAxisY);
  const unrotatedExactOffsetPerimeter = polyLine2Rotate(polyLine2Copy(offsetExactPerimeter), -angle);

  let offsetBoundingPerimeter: PolyLine2;

  // for avoiding regressions...
  if (hasDiagonalEdge(unrotatedExactOffsetPerimeter)) {
    offsetBoundingPerimeter = offsetRoofPerimeterOld(
      boundingPerimeter,
      startOverhang,
      0,
      endOverhang,
      leftOverhang,
      rightOverhang,
      polyLine2Edge(boundingPerimeter, startEdge + 1),
    );
  } else {
    // get the bbox of the offset exact perimeter
    const bbox = polyLine2AABB2(unrotatedExactOffsetPerimeter);

    // build offset rectangular perimeter
    offsetBoundingPerimeter = polyLine2Rotate(polyLine2FromAABB2(bbox), angle);
  }

  // group the different perimeters
  const perimeters = roofPerimetersNew(
    startEdge,
    peakDirection,
    polyLine2Copy(inputPerimeter),
    polyLine2Copy(boundingPerimeter),
    offsetBoundingPerimeter,
    exactPerimeter,
    offsetExactPerimeter,
    wallsPerimeter,
    offsetWallPerimeter,
    polyLine2Copy(wallsPerimeter),
    wallsPerimeterWRTOverhangDatum,
  );

  const newRectangularOverhangs = getRectangularOverhangs(
    boundingPerimeter,
    offsetBoundingPerimeter,
    startEdge,
    endEdge,
    exteriorWallOffsetFromOverhangDatum,
  );

  return {
    roofVolume: {
      perimeters,
      rectangularOverhangs: newRectangularOverhangs,
    },
  };
}
